import {
  BidRecommendationPackagingFormInputs
} from '../components/show/bid-recommendations/bid-recommendation-packaging-form-inputs';
import { deleteWithValidation, patchWithValidation, postWithValidation, putWithValidation, RequestOptions } from '@/lib/api-helpers';

type UpdatePackagingRequest = {
  property: keyof BidRecommendationPackagingFormInputs;
  value: string;
};

export const useBidRecommendationsService = () => {
  const createBidRecommendation = (bidAnalysisId: number, { title, isHistorical }: { title: string, isHistorical: boolean }): Promise<void> => {
    const options: RequestOptions<any> = {
      data: { title }
    };

    if (isHistorical) {
      return postWithValidation(`bid-analyses/${bidAnalysisId}/historical-recommendations`, options);
    }

    return postWithValidation(`bid-analyses/${bidAnalysisId}/recommendations`, options);
  };

  const createHistoricalBidRecommendationPackaging = (
    bidAnalysisId: number,
    recommendationId: number, { ndc, description }: { ndc: string, description: string }
  ): Promise<void> => {
    const options: RequestOptions<any> = {
      data: { ndc, description }
    };

    return postWithValidation(`bid-analyses/${bidAnalysisId}/historical-recommendations/${recommendationId}/packagings`, options);
  };

  const deleteBidRecommendation = (
    bidAnalysisId: number,
    bidRecommendationId: number,
    isHistorical: boolean
  ): Promise<void> => {
    if (isHistorical) {
      return deleteWithValidation(`bid-analyses/${bidAnalysisId}/historical-recommendations/${bidRecommendationId}`);
    }

    return deleteWithValidation(`bid-analyses/${bidAnalysisId}/recommendations/${bidRecommendationId}`);
  };

  const updateBidRecommendation = (
    bidAnalysisId: number,
    bidRecommendationId: number,
    { title }: { title: string },
    isHistorical: boolean
  ): Promise<{ title: string }> => {
    const options: RequestOptions<any> = {
      data: { title }
    };

    if (isHistorical) {
      return putWithValidation(`bid-analyses/${bidAnalysisId}/historical-recommendations/${bidRecommendationId}`, options)
        .then(() => ({ title: title }));
    }

    return putWithValidation(`bid-analyses/${bidAnalysisId}/recommendations/${bidRecommendationId}`, options)
      .then(() => ({ title: title }));
  };

  const updatePackaging = (
    bidAnalysisId: number,
    bidRecommendationId: number,
    packagingId: number,
    propertyKey: keyof BidRecommendationPackagingFormInputs,
    propertyValue: string
  ): Promise<any> => {
    const request: UpdatePackagingRequest = {
      property: propertyKey,
      value: propertyValue
    };

    const options: RequestOptions<any> = {
      data: { ...request }
    };

    return patchWithValidation(`bid-analyses/${bidAnalysisId}/recommendations/${bidRecommendationId}/packagings/${packagingId}`, options);
  };

  const updateHistoricalPackaging = (
    bidAnalysisId: number,
    bidRecommendationId: number,
    packagingId: number,
    propertyKey: keyof BidRecommendationPackagingFormInputs,
    propertyValue: string
  ): Promise<any> => {
    const request: UpdatePackagingRequest = {
      property: propertyKey,
      value: propertyValue
    };

    const options: RequestOptions<any> = {
      data: { ...request }
    };

    return patchWithValidation(`bid-analyses/${bidAnalysisId}/historical-recommendations/${bidRecommendationId}/packagings/${packagingId}`, options);
  };

  const deleteHistoricalPackagings = (
    bidAnalysisId: number,
    bidRecommendationId: number,
    packagingIds: number[],
  ): Promise<any> => {
    const request = {
      packagingIds
    };

    const options: RequestOptions<any> = {
      data: { ...request }
    };

    return deleteWithValidation(`bid-analyses/${bidAnalysisId}/historical-recommendations/${bidRecommendationId}/packagings`, options);
  };

  return {
    createBidRecommendation,
    createHistoricalBidRecommendationPackaging,
    deleteBidRecommendation,
    deleteHistoricalPackagings,
    updateBidRecommendation,
    updatePackaging,
    updateHistoricalPackaging
  };
};
